/** Player settings — pure data + sanitising. Applied to the DOM by src/ui. */

export type Quality = 'low' | 'med' | 'high';
export type PunchMode = 'hot' | 'preview';

export interface Settings {
  /** 0..1 */
  masterVolume: number;
  quality: Quality;
  /** 'hot' = number keys punch straight to PGM; 'preview' = number keys load PVW, Space takes. */
  punchMode: PunchMode;
  reducedMotion: boolean;
  /** Producer coaching tips in Rookie matches (off for Pro/Legend regardless). */
  coaching: boolean;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  masterVolume: 0.8,
  quality: 'high',
  punchMode: 'hot',
  reducedMotion: false,
  coaching: true,
});

const QUALITIES: readonly Quality[] = ['low', 'med', 'high'];
const PUNCH: readonly PunchMode[] = ['hot', 'preview'];

/** Coerce anything (old saves, hand-edited JSON) into a valid Settings object. */
export function sanitizeSettings(raw: unknown): Settings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const vol = typeof r.masterVolume === 'number' && Number.isFinite(r.masterVolume)
    ? Math.min(1, Math.max(0, r.masterVolume)) : DEFAULT_SETTINGS.masterVolume;
  return {
    masterVolume: vol,
    quality: QUALITIES.includes(r.quality as Quality) ? (r.quality as Quality) : DEFAULT_SETTINGS.quality,
    punchMode: PUNCH.includes(r.punchMode as PunchMode) ? (r.punchMode as PunchMode) : DEFAULT_SETTINGS.punchMode,
    reducedMotion: typeof r.reducedMotion === 'boolean' ? r.reducedMotion : DEFAULT_SETTINGS.reducedMotion,
    coaching: typeof r.coaching === 'boolean' ? r.coaching : DEFAULT_SETTINGS.coaching,
  };
}
